import { useMemo, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";
import { useWorkstreamsByProjectQuery } from "@/hooks/queries/useWorkstreamsQuery";
import { AlertTriangle } from "lucide-react";
import {
  AgentType,
  AGENT_TYPE_LABELS,
  AGENT_TYPE_TRANSLATION_KEYS,
  AGENT_TYPE_ICONS,
  safeParseJsonArray,
} from "@/types/workstream";
import type { WorkstreamFinding } from "@/types/workstream";

interface RisksTabProps {
  projectId: string;
}

interface AgentRiskGroup {
  agentType: number;
  totalScore: number;
}

const UTILITY_AGENT_TYPES = new Set<number>([
  AgentType.Summarization,
  AgentType.RedFlagClassifier,
  AgentType.GapDetector,
  AgentType.ContradictionDetector,
  AgentType.CrossDocSynthesis,
]);

const RisksTab = ({ projectId }: RisksTabProps) => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  const { data: workstreams, isLoading } = useWorkstreamsByProjectQuery(Number(projectId));

  const getAgentLabel = useCallback((agentType: number): string => {
    const translationKey = AGENT_TYPE_TRANSLATION_KEYS[agentType];
    if (translationKey) {
      const translated = t(translationKey as Parameters<typeof t>[0]);
      if (translated !== translationKey) return translated;
    }
    return AGENT_TYPE_LABELS[agentType] ?? `Agent ${agentType}`;
  }, [t]);

  // Group workstreams by agent type and sum risk scores (include all agent types, even null scores)
  const riskGroups = useMemo<AgentRiskGroup[]>(() => {
    if (!workstreams) return [];
    const groupMap = new Map<number, AgentRiskGroup>();
    for (const ws of workstreams) {
      if (UTILITY_AGENT_TYPES.has(ws.agentType)) continue;
      const existing = groupMap.get(ws.agentType);
      if (existing) {
        if (ws.riskScore !== null) existing.totalScore += ws.riskScore;
      } else {
        groupMap.set(ws.agentType, {
          agentType: ws.agentType,
          totalScore: ws.riskScore ?? 0,
        });
      }
    }
    return [...groupMap.values()].sort((a, b) => b.totalScore - a.totalScore);
  }, [workstreams]);

  // Flatten all findings across every workstream for severity counting
  const allWorkstreamFindings = useMemo<WorkstreamFinding[]>(() => {
    if (!workstreams) return [];
    return workstreams.flatMap(ws => safeParseJsonArray<WorkstreamFinding>(ws.findingsJson));
  }, [workstreams]);

  // Stat card counts — sourced directly from finding severity fields
  const totalFindingsCount = allWorkstreamFindings.length;
  const criticalCount = allWorkstreamFindings.filter(f => f.severity?.toLowerCase() === "critical").length;
  const highCount = allWorkstreamFindings.filter(f => f.severity?.toLowerCase() === "high").length;
  const mediumCount = allWorkstreamFindings.filter(f => f.severity?.toLowerCase() === "medium").length;
  const lowCount = allWorkstreamFindings.filter(f => f.severity?.toLowerCase() === "low").length;
  const infoCount = allWorkstreamFindings.filter(f => f.severity?.toLowerCase() === "info").length;

  const getScoreColor = (score: number) => {
    if (score >= 7) return "bg-red-500";
    if (score >= 4) return "bg-yellow-500";
    return "bg-green-500";
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <AlertTriangle size={20} className="text-muted-foreground" />
              <div>
                <p className="text-2xl font-bold">{totalFindingsCount}</p>
                <p className="text-sm text-muted-foreground">{t("totalRisks")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-red-600" />
              <div>
                <p className="text-2xl font-bold">{criticalCount}</p>
                <p className="text-sm text-muted-foreground">{t("riskCritical")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-orange-500" />
              <div>
                <p className="text-2xl font-bold">{highCount}</p>
                <p className="text-sm text-muted-foreground">{t("riskHigh")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-yellow-500" />
              <div>
                <p className="text-2xl font-bold">{mediumCount}</p>
                <p className="text-sm text-muted-foreground">{t("riskMedium")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-green-500" />
              <div>
                <p className="text-2xl font-bold">{lowCount}</p>
                <p className="text-sm text-muted-foreground">{t("riskLow")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-blue-500" />
              <div>
                <p className="text-2xl font-bold">{infoCount}</p>
                <p className="text-sm text-muted-foreground">{t("riskInfo")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Risk Register Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("riskRegister")}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("riskTitle")}</TableHead>
                <TableHead className="text-center">{t("score")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {riskGroups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} className="text-center text-muted-foreground py-8">
                    {t("noWorkstreams")}
                  </TableCell>
                </TableRow>
              ) : (
                riskGroups.map((group) => {
                  const IconComponent = AGENT_TYPE_ICONS[group.agentType];
                  return (
                    <TableRow key={group.agentType}>
                      <TableCell>
                        <div className="flex items-center gap-2 font-medium">
                          {IconComponent && <IconComponent size={18} />}
                          {getAgentLabel(group.agentType)}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-white font-bold text-sm ${getScoreColor(group.totalScore)}`}>
                          {group.totalScore}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default RisksTab;
